// wttr.in API - Free, no API key required
// Documentation: https://github.com/chubin/wttr.in
export type WeatherData = {
  current_condition: Array<{
    temp_C: string;
    FeelsLikeC: string;
    humidity: string;
    windspeedKmph: string;
    weatherDesc: Array<{ value: string }>;
    weatherIconUrl: Array<{ value: string }>;
  }>;
  nearest_area: Array<{
    areaName: Array<{ value: string }>;
  }>;
};

export const weatherService = {
  async getWeatherByCity(city: string): Promise<WeatherData> {
    // wttr.in - Free weather API, no API key required
    // Returns JSON format, accepts city names directly
    const API_URL = 'https://wttr.in';
    const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
    const cacheKey = `weather:wttr:${city.toLowerCase().trim()}`;

    // localStorage cache (30 min)
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { ts: number; data: WeatherData } | null;
        if (parsed?.ts && parsed?.data) {
          const age = Date.now() - parsed.ts;
          if (age >= 0 && age < CACHE_TTL_MS) {
            return parsed.data;
          }
        }
      }
    } catch {
      // ignore cache errors (JSON parsing / private mode / storage disabled)
    }

    try {
      const response = await fetch(
        `${API_URL}/${encodeURIComponent(city)}?format=j1&lang=fr`,
        {
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Ville "${city}" non trouvée`);
        }
        throw new Error(`Erreur API météo: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Validate response structure
      if (!data.current_condition || !Array.isArray(data.current_condition) || data.current_condition.length === 0) {
        throw new Error('Format de réponse invalide de l\'API météo');
      }

      // Save to cache
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data }));
      } catch {
        // ignore storage quota / privacy errors
      }

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur réseau lors de la récupération des données météo');
    }
  },
};
